import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface OnboardingState {
  step: string;
  version: number;
  workspaceType: 'personal' | 'team' | 'enterprise' | null;
  selectedOutcomes: string[];
  isComplete: boolean;
}

/** ONB-04: the backend's per-address outcome of the invites step. */
export interface InviteResult {
  email: string;
  status: 'invited' | 'skipped';
  code?: string;
  message?: string;
}

/** The plan onboarding will finish on, as /api/onboarding/pricing-summary reports it. */
interface PlanSeats {
  name: string;
  includedSeats: number;
}

/**
 * InviteTeammates page (Step 4 of v2 onboarding - team/enterprise only)
 *
 * Allows users to invite team members via email.
 * Personal workspaces skip this step.
 */
export function InviteTeammatesPage() {
  const [emails, setEmails] = useState<string[]>(['', '', '']);
  const [state, setState] = useState<OnboardingState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  // Addresses that were NOT invited, with the reason. Shown instead of
  // silently moving on (ONB-04); null until a send produced skips.
  const [skippedResults, setSkippedResults] = useState<InviteResult[] | null>(null);
  const [invitedCount, setInvitedCount] = useState(0);
  const [planSeats, setPlanSeats] = useState<PlanSeats | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  // Load onboarding state
  useEffect(() => {
    async function loadData() {
      try {
        const stateRes = await fetch('/api/onboarding/state', { credentials: 'include' });
        const stateData = await stateRes.json();

        if (stateRes.ok && stateData.state) {
          setState(stateData.state);

          // Redirect personal-typed business workspaces straight to complete.
          if (stateData.state.workspaceType === 'personal') {
            navigate(onboardingPath('business', 'complete', continueUrl));
          }
        }
      } catch {
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    }

    // The seats the selected plan includes — the same figure the pricing step
    // showed — for the non-blocking notice below. Best-effort: no notice when
    // it cannot be read. Display only; seat limits are enforced server-side.
    async function loadPlanSeats() {
      try {
        const res = await fetch('/api/onboarding/pricing-summary', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        const plan = data?.pricing?.plan;
        if (plan && typeof plan.includedSeats === 'number' && plan.includedSeats > 0) {
          setPlanSeats({ name: String(plan.name ?? 'selected'), includedSeats: plan.includedSeats });
        }
      } catch {
        // No notice.
      }
    }

    loadData();
    loadPlanSeats();
  }, [navigate, continueUrl]);

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const addEmailField = () => {
    if (emails.length < 10) {
      setEmails([...emails, '']);
    }
  };

  const removeEmailField = (index: number) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
    }
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.trim() === '' || emailRegex.test(email.trim());
  };

  // Get valid non-empty emails
  const getValidEmails = (): string[] => {
    return emails.filter((e) => e.trim() !== '' && isValidEmail(e)).map((e) => e.trim());
  };

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors on back navigation
    }

    navigate(onboardingPath('business', 'review', continueUrl));
  };

  const handleContinue = async (skip: boolean = false) => {
    setIsSaving(true);
    setError('');
    setSkippedResults(null);

    const validEmails = skip ? [] : getValidEmails();

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: 'invites',
          data: { emails: validEmails, skip },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send invites');
        return;
      }

      // Say which addresses were not invited (already a member, already
      // invited, no seat, …) rather than continuing as if all were. The step
      // has advanced either way; the user can fix an address and send again,
      // or continue.
      const results: InviteResult[] = Array.isArray(data.inviteResults) ? data.inviteResults : [];
      const skipped = results.filter((r) => r.status === 'skipped');
      if (!skip && skipped.length > 0) {
        setInvitedCount(results.filter((r) => r.status === 'invited').length);
        setSkippedResults(skipped);
        return;
      }

      navigate(onboardingPath('business', 'complete', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check for invalid emails
  const hasInvalidEmails = emails.some((e) => e.trim() !== '' && !isValidEmail(e));
  const validEmailCount = getValidEmails().length;
  // Non-blocking: the owner takes one of the plan's included seats.
  const exceedsIncludedSeats = planSeats !== null && 1 + validEmailCount > planSeats.includedSeats;

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="invites" workspaceType={state?.workspaceType} onboardingMode="business">
        <div className="rounded-3xl bg-white p-10 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // Seats the owner plus these invitations would take, for the side panel.
  const seatsTaken = 1 + validEmailCount;

  return (
    <OnboardingLayout currentStep="invites" workspaceType={state?.workspaceType} onboardingMode="business" wide>
      <div className="rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5">
        <div className="grid lg:grid-cols-12">
          <section className="min-w-0 p-4 sm:p-10 lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Your team</p>
            <h1 className="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-3xl">Invite Your Team</h1>
            <p className="mt-1 text-sm text-gray-500 sm:mt-1.5 sm:text-base">
              Get your team on board to make the most of your workspace
            </p>

            {/* Email inputs */}
            <div className="mt-7 space-y-2.5">
              {emails.map((email, index) => {
                const invalid = email.trim() !== '' && !isValidEmail(email);
                const filled = email.trim() !== '' && !invalid;
                return (
                  <div key={index} className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`flex h-11 w-11 flex-none items-center justify-center rounded-xl text-sm font-semibold uppercase transition-colors ${
                        filled
                          ? 'bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {filled ? (
                        email.trim().charAt(0)
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => updateEmail(index, e.target.value)}
                        placeholder="colleague@company.com"
                        className={`w-full rounded-xl border px-4 py-3 text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                          invalid ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      />
                    </div>
                    {emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmailField(index)}
                        className="flex h-10 w-10 flex-none items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Remove email"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add more button */}
            {emails.length < 10 && (
              <button
                type="button"
                onClick={addEmailField}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 py-2.5 font-medium text-gray-500 transition-colors hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add another
              </button>
            )}

            {/* Info text */}
            <p className="mt-4 text-sm text-gray-400">
              They'll receive an email invitation to join your workspace
            </p>

            {/* Seat info. This used to promise "Trial seats are unlimited", which
                nothing enforces: seats are checked against the plan when an
                invitation is ACCEPTED, trial or not. Now it states the plan's
                included seats and warns — without blocking — when the invites
                go past them. */}
            {planSeats && (
              <div
                className={`mt-6 flex items-start gap-3 rounded-xl border p-4 ${
                  exceedsIncludedSeats ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'
                }`}
                data-testid={exceedsIncludedSeats ? 'invite-seat-warning' : 'invite-seat-info'}
                role={exceedsIncludedSeats ? 'status' : undefined}
              >
                <span
                  aria-hidden="true"
                  className={`flex h-7 w-7 flex-none items-center justify-center rounded-full ${
                    exceedsIncludedSeats ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={exceedsIncludedSeats ? 'M12 9v4m0 4h.01' : 'M5 13l4 4L19 7'}
                    />
                  </svg>
                </span>
                <div className="text-sm">
                  <p className={`font-medium ${exceedsIncludedSeats ? 'text-amber-900' : 'text-emerald-900'}`}>
                    Your {planSeats.name} plan includes {planSeats.includedSeats}{' '}
                    {planSeats.includedSeats === 1 ? 'seat' : 'seats'}, including yours
                  </p>
                  {exceedsIncludedSeats && (
                    <p className="mt-1 text-amber-800">
                      You&apos;re inviting more teammates than that. You can still send the invitations, but
                      some teammates may not be able to join until seats are added.
                    </p>
                  )}
                </div>
              </div>
            )}

            {skippedResults && (
              <div
                className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
                data-testid="invite-results"
                role="status"
              >
                <p className="text-sm font-medium text-amber-900">
                  {invitedCount > 0
                    ? `${invitedCount} invitation${invitedCount !== 1 ? 's' : ''} sent. `
                    : ''}
                  {skippedResults.length === 1
                    ? '1 address was not invited:'
                    : `${skippedResults.length} addresses were not invited:`}
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-800">
                  {skippedResults.map((r) => (
                    <li key={`${r.email}-${r.code ?? ''}`} data-code={r.code}>
                      <span className="font-medium">{r.email}</span>
                      {' — '}
                      {r.message || 'Invitation could not be sent'}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => navigate(onboardingPath('business', 'complete', continueUrl))}
                  className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700"
                >
                  Continue
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </section>

          <aside className="relative hidden overflow-hidden rounded-r-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-10 text-white lg:col-span-5 lg:flex lg:flex-col">
            <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <svg className="h-6 w-6 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300">Ready to send</p>
              <p className="mt-1 text-5xl font-semibold tracking-tight">{validEmailCount}</p>
              <p className="mt-1 text-sm text-slate-300">
                {validEmailCount === 1 ? 'invitation' : 'invitations'}
              </p>

              {planSeats && (
                <div className="mt-6">
                  <div className="flex items-baseline justify-between text-xs text-slate-300">
                    <span>Seats with you included</span>
                    <span className={exceedsIncludedSeats ? 'font-semibold text-amber-300' : 'font-semibold text-white'}>
                      {seatsTaken} / {planSeats.includedSeats}
                    </span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
                    <div
                      className={`h-full rounded-full transition-all ${exceedsIncludedSeats ? 'bg-amber-400' : 'bg-emerald-400'}`}
                      style={{ width: `${Math.min(100, (seatsTaken / planSeats.includedSeats) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <ul className="mt-8 space-y-4 border-t border-white/10 pt-6 text-sm text-slate-200">
                {[
                  'Each teammate gets their own email invitation',
                  'Seats are checked when a teammate accepts',
                  'Skip for now and invite people whenever you are ready',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-400/20 text-emerald-300">
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        <div className="sticky bottom-0 z-10 flex flex-row items-center justify-between gap-2 rounded-b-3xl border-t border-gray-100 bg-white px-3 py-2 sm:px-8 sm:py-4">
          <button
            type="button"
            onClick={handleBack}
            className="px-3 py-2.5 font-medium text-gray-600 transition-colors hover:text-gray-900 sm:px-6"
          >
            Back
          </button>
          <div className="flex flex-row items-center gap-1 sm:gap-3">
            <button
              type="button"
              onClick={() => handleContinue(true)}
              disabled={isSaving}
              className="px-3 py-2.5 font-medium text-gray-600 transition-colors hover:text-gray-900 sm:px-6"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={() => handleContinue(false)}
              disabled={isSaving || hasInvalidEmails || validEmailCount === 0}
              className="rounded-xl bg-emerald-600 px-5 py-3 font-semibold text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-7"
            >
              {isSaving
                ? 'Sending...'
                : validEmailCount > 0
                ? `Send ${validEmailCount} invite${validEmailCount !== 1 ? 's' : ''}`
                : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
}
